// ui/src/components/calendar/CalendarGrid.tsx
//
// The one shared calendar grid rendered by the admin, staff and parent
// calendar pages (Tasks 20-22). Presentational only: it renders whatever
// `days` it's given and reports clicks/navigation via callbacks — the host
// page owns date-range state, data fetching and the closure/create-event
// panels.
import React from 'react';
import CalendarDayCell from './CalendarDayCell';
import type { CalendarDay, CalendarGridProps } from './types';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** JS getDay() is Sunday-first (0-6); convert to Monday-first (0-6, Mon=0). */
function mondayFirstIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

function emptyDay(date: Date): CalendarDay {
  return { date: toIsoDate(date), classes: [] };
}

/**
 * Build a Monday-first month grid: pad leading days from the previous month
 * and trailing days from the next month so the grid is always a whole
 * number of weeks. `days` supplies real data for the visible month; any
 * date in `days` outside the padded range is ignored (host pages fetch by
 * the visible range, so this shouldn't happen in practice).
 */
function buildMonthCells(
  days: CalendarDay[]
): { date: Date; inCurrentPeriod: boolean; day: CalendarDay }[] {
  if (days.length === 0) return [];

  const byDate = new Map(days.map((d) => [d.date, d]));
  const firstOfMonth = toDate(days[0].date);
  const lastOfMonth = toDate(days[days.length - 1].date);

  const leadingPad = mondayFirstIndex(firstOfMonth);
  const trailingPad = 6 - mondayFirstIndex(lastOfMonth);

  const cells: { date: Date; inCurrentPeriod: boolean; day: CalendarDay }[] = [];

  for (let i = leadingPad; i > 0; i--) {
    const date = new Date(firstOfMonth);
    date.setDate(date.getDate() - i);
    cells.push({ date, inCurrentPeriod: false, day: emptyDay(date) });
  }

  for (const day of days) {
    cells.push({ date: toDate(day.date), inCurrentPeriod: true, day });
  }

  for (let i = 1; i <= trailingPad; i++) {
    const date = new Date(lastOfMonth);
    date.setDate(date.getDate() + i);
    cells.push({
      date,
      inCurrentPeriod: false,
      day: byDate.get(toIsoDate(date)) ?? emptyDay(date),
    });
  }

  return cells;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  dayAction,
  mode,
  days,
  onDayClick,
  onNavigate,
  onModeChange,
  highlightedDates,
}) => {
  const todayIso = toIsoDate(new Date());
  const cells =
    mode === 'month'
      ? buildMonthCells(days)
      : days.map((day) => ({ date: toDate(day.date), inCurrentPeriod: true, day }));

  return (
    <div data-testid="calendar-grid" className="w-full">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="calendar-prev"
            onClick={() => onNavigate?.('prev')}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Prev
          </button>
          <button
            type="button"
            data-testid="calendar-today"
            onClick={() => onNavigate?.('today')}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Today
          </button>
          <button
            type="button"
            data-testid="calendar-next"
            onClick={() => onNavigate?.('next')}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Next
          </button>
        </div>

        <div
          className="inline-flex rounded-md shadow-sm"
          role="group"
          aria-label="Calendar view mode"
        >
          <button
            type="button"
            data-testid="calendar-mode-month"
            onClick={() => onModeChange?.('month')}
            aria-pressed={mode === 'month'}
            className={`rounded-l-md border px-3 py-1.5 text-sm font-medium ${
              mode === 'month'
                ? 'border-primary-600 bg-primary-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Month
          </button>
          <button
            type="button"
            data-testid="calendar-mode-week"
            onClick={() => onModeChange?.('week')}
            aria-pressed={mode === 'week'}
            className={`rounded-r-md border-b border-r border-t px-3 py-1.5 text-sm font-medium ${
              mode === 'week'
                ? 'border-primary-600 bg-primary-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Week
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEKDAY_LABELS.map((label, index) => (
          <div
            key={label}
            data-testid={`calendar-weekday-${index}`}
            className="px-1 py-1 text-center text-xs font-semibold uppercase text-gray-500"
          >
            {label}
          </div>
        ))}

        {cells.map(({ date, inCurrentPeriod, day }) => (
          <CalendarDayCell
            key={day.date}
            day={day}
            inCurrentPeriod={inCurrentPeriod}
            isToday={day.date === todayIso}
            isHighlighted={highlightedDates?.has(day.date) ?? false}
            onClick={onDayClick}
            action={dayAction}
          />
        ))}
      </div>
    </div>
  );
};

export default CalendarGrid;
